<i18n>
{
  "en": {
    "productBoost": "Product Boost",
    "addProductBoost": "Add product boost",
    "deleteTitle": "Are you sure you want to delete this product boost?",
    "deleteError": "There was an error deleting the boost",
    "createError": "There was an error creating the boost",
    "delete": "Delete",
    "noRules": "No product boosts available",
    "duplicateError": "A product boost with this product already exists",
    "requestError": "There was an error loading needing data for product boosts",
    "periodError": "All prediction periods of the product boost must be fully validated"
  },
  "es": {
    "productBoost": "Product Boost",
    "addProductBoost": "Añadir product boost",
    "deleteTitle": "¿Estás seguro de que quieres borrar este product boost?",
    "deleteError": "Ha habido un error borrando el product boost",
    "createError": "Ha habido un error creando el product boost",
    "delete": "Borrar",
    "noRules": "No hay promociones de producto disponibles",
    "duplicateError": "Ya existe un product boost para este producto en este rango de tiempo",
    "requestError": "Ha habido un error cargando los datos de los product boosts",
    "periodError": "Todos los periodos de predicción del product boost deben estar completamente validados"
  }
}
</i18n>
<template>
  <v-container class="mb-10">
    <div class="d-flex justify-space-between">
      <product-boost-legend/>
      <div class="text-center font-weight-bold ma-1 text-md-h6 text-body-1"> {{ $t('productBoost') }}</div>
      <v-tooltip top>
        <template v-slot:activator="{ on, attrs }">
          <v-btn icon class="mr-2" v-on="on" v-bind="attrs" @click="openCreateModal">
            <v-icon color="grey" large>mdi-plus</v-icon>
          </v-btn>
        </template>
        <span>{{ $t('addProductBoost') }}</span>
      </v-tooltip>
    </div>
    <dynameat-skeleton v-if="isLoading || !pointOfSaleSettings" skeleton-type="manualConfigList"
                       :container="true"/>
    <dynameat-confirmation-modal :visible="showSpinner" :is-loading="showSpinner"/>
    <div v-if="emptyProductBoostList && !isLoading" class="d-flex justify-space-around grey--text mt-5">
      <span>{{ $t('noRules') }}</span>
    </div>
    <div v-if="!isLoading && pointOfSaleSettings">
      <v-list v-for="productBoost in productBoostList" :key="productBoost.guid">
        <product-boost-card :productBoost="productBoost" @delete="onOpenDeleteModal(productBoost)"/>
      </v-list>
      <product-boost-modal :visible="createModalVisible" :productList="managedProductList" :serviceList="serviceList"
                           @confirm="onConfirmCreateModal" @cancel="onCancelCreateModal"/>
    </div>
    <dynameat-confirmation-modal :visible="deleteModalVisible" :title="$t('deleteTitle')"
                                 :description="deletedBoostString" :confirm-text="$t('delete')"
                                 @confirm="onConfirmDeleteModal" @cancel="onCloseDeleteModal"/>
  </v-container>
</template>

<script>

import Vue from 'vue';
import Component from 'vue-class-component';
import { Components, MapActions, MapGetters } from '@core/helpers/decorators';
import DynameatConfirmationModal from '@dynameat/components/DynameatConfirmationModal';
import DynameatSkeleton from '@dynameat/components/DynameatSkeleton';
import ProductBoostLegend from '@manualConfig/views/productBoost/components/ProductBoostLegend';
import { createErrorNotification } from '@ulibs/unnotificationsqueue';
import { Watch } from 'vue-property-decorator';
import { PERIOD_NOT_CONFIRMED, PRODUCT_BOOST_CONFLICT } from '@manualConfig/views/productBoost/api';
import ProductBoostCard from './components/ProductBoostCard.vue';
import ProductBoostModal from './components/ProductBoostModal.vue';

@Component
@Components({
  ProductBoostModal, ProductBoostCard, ProductBoostLegend, DynameatConfirmationModal, DynameatSkeleton,
})
@MapGetters(['managedProductList', 'productBoostList', 'isLoading', 'pointOfSaleSettings', 'serviceList'])
@MapActions(['masterProductListRequest', 'createProductBoostRequest', 'removeProductBoostRequest',
  'setLoading', 'addNotification', 'productBoostListRequest', 'resetProductBoostedData'])
export default class ProductBoostView extends Vue {
  createModalVisible = false;

  deleteModalVisible = false;

  productBoostToDelete = null;

  showSpinner = false;

  get deletedBoostString() {
    if (this.productBoostToDelete) {
      return `${this.managedProductList.find((product) => product.guid === this.productBoostToDelete.productGuid)?.commercial_name} (${this.productBoostToDelete.startDate.toLocaleDateString()} - ${this.productBoostToDelete.endDate.toLocaleDateString()})`;
    }
    return '';
  }

  get emptyProductBoostList() {
    return !this.productBoostList || this.productBoostList.length < 1;
  }

  async created() {
    await this.getProductBoostList();
    await this.loadManagedProductListData();
  }

  async getProductBoostList() {
    try {
      this.setLoading(1);
      await this.productBoostListRequest(this.$route.params.pointOfSaleGuid);
    } catch {
      this.addNotification(createErrorNotification(this.$t('requestError')));
    } finally {
      this.setLoading(-1);
    }
  }

  async loadManagedProductListData() {
    try {
      await this.masterProductListRequest(this.$route.params.pointOfSaleGuid);
    } catch (e) {
      this.addNotification(createErrorNotification(this.$t('productListError')));
    }
  }

  openCreateModal() {
    this.createModalVisible = true;
  }

  onCancelCreateModal() {
    this.createModalVisible = false;
  }

  async onConfirmCreateModal(productBoost) {
    this.createModalVisible = false;
    try {
      this.showSpinner = true;
      await this.setLoading(1);
      await this.createProductBoostRequest(productBoost);
      await this.resetProductBoostedData();
    } catch (err) {
      if (err.response.status === 409 && err.response.data === PRODUCT_BOOST_CONFLICT) {
        this.addNotification(createErrorNotification(this.$t('duplicateError')));
      } else if (err.response.status === 409 && err.response.data === PERIOD_NOT_CONFIRMED) {
        this.addNotification(createErrorNotification(this.$t('periodError')));
      } else {
        this.addNotification(createErrorNotification(this.$t('createError')));
      }
    } finally {
      this.showSpinner = false;
      await this.setLoading(-1);
    }
  }

  onOpenDeleteModal(productBoost) {
    this.productBoostToDelete = productBoost;
    this.deleteModalVisible = true;
  }

  onCloseDeleteModal() {
    this.productBoostToDelete = null;
    this.deleteModalVisible = false;
  }

  async onConfirmDeleteModal() {
    this.deleteModalVisible = false;
    try {
      await this.setLoading(1);
      await this.removeProductBoostRequest(this.productBoostToDelete.guid);
      await this.resetProductBoostedData();
    } catch (err) {
      this.addNotification(createErrorNotification(this.$t('deleteError')));
    } finally {
      await this.setLoading(-1);
    }
  }

  @Watch('$route')
  async onRouteChange(to, from) {
    if (to.params.pointOfSaleGuid !== from.params.pointOfSaleGuid) {
      await this.getProductBoostList();
      await this.loadManagedProductListData();
    }
  }
}

</script>
